import type {
  ApiUpdateAuthorizationError,
  ApiUpdateAuthorizationState,
  ApiUpdateConnectionState,
  ApiUpdateCurrentUser,
  ApiUpdateServerTimeOffset,
  ApiUpdateSession,
} from '../../../api/types';
import type { LangCode } from '../../../types';
import type { RequiredGlobalActions } from '../../index';
import type { ActionReturnType, GlobalState } from '../../types';

import { reportAttrLogin } from '../../../util/attrReport';
import {
  getHiddenMsSinceLoad, recordAuthDiagnostic, setClientDiagnosticsUserId,
} from '../../../util/clientDiagnostics';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { unique } from '../../../util/iteratees';
import { callEventLoginNative, callEventLogoutNative, getChannelCode } from '../../../util/nativeBridge';
import { oldSetLanguage } from '../../../util/oldLangProvider';
import { reportPushApp } from '../../../util/reportPushApp';
import { setServerTimeOffset } from '../../../util/serverTime';
import { clearStoredSession, hasStoredSession, updateSessionUserId } from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import { isChatChannel, isChatSuperGroup } from '../../helpers';
import {
  addActionHandler,
  getGlobal,
  setGlobal,
} from '../../index';
import { updateUser, updateUserFullInfo } from '../../reducers';
import { selectTabState } from '../../selectors';
import { selectSharedSettings } from '../../selectors/sharedState';

const HAD_STORED_SESSION_AT_LOAD = hasStoredSession();

function isAuthUiActive(authState: GlobalState['authState']) {
  return authState === 'authorizationStateWaitPhoneNumber'
    || authState === 'authorizationStateWaitCode'
    || authState === 'authorizationStateWaitPassword'
    || authState === 'authorizationStateWaitRegistration'
    || authState === 'authorizationStateWaitQrCode';
}

addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  switch (update['@type']) {
    case 'updateApiReady':
      onUpdateApiReady(global);
      break;

    case 'updateAuthorizationState':
      onUpdateAuthorizationState(global, update);
      break;

    case 'updateAuthorizationError':
      onUpdateAuthorizationError(global, update);
      break;

    case 'updateConnectionState':
      onUpdateConnectionState(global, actions, update);
      break;

    case 'updateSession':
      onUpdateSession(global, actions, update);
      break;

    case 'updateServerTimeOffset':
      onUpdateServerTimeOffset(update);
      break;

    case 'updateCurrentUser':
      onUpdateCurrentUser(global, update);
      break;

    case 'updateAuthKeyUnregistered': {
      callEventLogoutNative({ uid: global.currentUserId });
      clearStoredSession();
      if (isAuthUiActive(global.authState)) {
        break;
      }
      setTimeout(() => window.location.reload(), 500);
      break;
    }

    case 'updateConnectionTimeoutReload': {
      if (isAuthUiActive(global.authState)) {
        break;
      }
      try {
        const key = 'ssgram_conn_timeout_count';
        const count = Number(sessionStorage.getItem(key) || '0') + 1;
        if (count >= 3) {
          sessionStorage.setItem(key, '0');
          setTimeout(() => window.location.reload(), 800);
        } else {
          sessionStorage.setItem(key, String(count));
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
      break;
    }

    case 'requestReconnectApi':
      global = {
        ...global,
        isSynced: false,
        chats: {
          ...global.chats,
          isFullyLoaded: {},
        },
      };
      setGlobal(global);

      onUpdateConnectionState(global, actions, {
        '@type': 'updateConnectionState',
        connectionState: 'connectionStateConnecting',
      });
      actions.initApi();
      break;

    case 'requestSync':
      actions.sync();
      break;

    case 'updateFetchingDifference':
      global = { ...global, isFetchingDifference: update.isFetching };
      setGlobal(global);
      break;

    case 'error': {
      if (actions.showDialog) {
        Object.values(global.byTabId).forEach(({ id: tabId }) => {
          actions.showDialog({ data: update.error, tabId });
        });
      }
      break;
    }
  }
});

function onUpdateApiReady<T extends GlobalState>(global: T) {
  void oldSetLanguage(selectSharedSettings(global).language as LangCode);
}

function onUpdateAuthorizationState<T extends GlobalState>(global: T, update: ApiUpdateAuthorizationState) {
  global = getGlobal();

  const wasAuthReady = global.authState === 'authorizationStateReady';
  const authState = update.authorizationState;

  global = {
    ...global,
    authState,
    authIsLoading: false,
  };
  setGlobal(global);

  global = getGlobal();

  switch (authState) {
    case 'authorizationStateLoggingOut':
      void forceWebsync(false);

      global = {
        ...global,
        isLoggingOut: true,
      };
      setGlobal(global);
      break;
    case 'authorizationStateWaitPhoneNumber':
      break;
    case 'authorizationStateReady': {
      if (wasAuthReady) {
        break;
      }

      {
        const elapsedMs = Math.round(performance.now());
        const hiddenMs = Math.round(getHiddenMsSinceLoad());

        recordAuthDiagnostic('session_restore', {
          success: true,
          duration: Math.max(0, elapsedMs - hiddenMs),
          detail: `raw:${elapsedMs} hidden:${hiddenMs} stored:${HAD_STORED_SESSION_AT_LOAD ? 1 : 0}`,
        });
      }

      void forceWebsync(true);

      global = {
        ...global,
        isLoggingOut: false,
      };
      setGlobal(global);

      break;
    }
  }
}

function onUpdateAuthorizationError<T extends GlobalState>(global: T, update: ApiUpdateAuthorizationError) {
  global = {
    ...global,
  };
  global.authErrorKey = update.errorKey;
  setGlobal(global);
}

function onUpdateConnectionState<T extends GlobalState>(
  global: T, actions: RequiredGlobalActions, update: ApiUpdateConnectionState,
) {
  const { connectionState } = update;

  global = getGlobal();
  const tabState = selectTabState(global, getCurrentTabId());
  if (connectionState === 'connectionStateReady' && tabState.isMasterTab && tabState.multitabNextAction) {
    // @ts-ignore
    actions[tabState.multitabNextAction.action](tabState.multitabNextAction.payload);
    actions.clearMultitabNextAction({ tabId: tabState.id });
  }

  if (connectionState === global.connectionState) {
    return;
  }

  global = {
    ...global,
    connectionState,
  };
  setGlobal(global);

  if (global.isSynced) {
    const channelStackIds = Object.values(global.byTabId)
      .flatMap((tab) => tab.messageLists || [])
      .map((messageList) => messageList?.chatId)
      .filter((chatId): chatId is string => Boolean(chatId))
      .filter((chatId) => {
        const chat = global.chats.byId[chatId];
        return chat && (isChatChannel(chat) || isChatSuperGroup(chat));
      });
    if (connectionState === 'connectionStateReady' && channelStackIds.length) {
      unique(channelStackIds).forEach((chatId) => {
        actions.requestChannelDifference({ chatId });
      });
    }
  }

  if (connectionState === 'connectionStateBroken') {
    actions.saveSession({ sessionData: undefined });
    actions.signOut({ forceInitApi: true });
  }
}

function onUpdateSession<T extends GlobalState>(global: T, actions: RequiredGlobalActions, update: ApiUpdateSession) {
  const { sessionData } = update;
  const { authRememberMe, authState } = global;
  const isEmpty = !sessionData || !sessionData.mainDcId;

  const isTest = sessionData?.isTest;
  if (isTest) {
    global = {
      ...global,
      config: {
        ...global.config,
        isTestServer: isTest,
      },
    };
    setGlobal(global);
  }

  if (!authRememberMe || isEmpty) {
    return;
  }

  if (authState !== 'authorizationStateReady') {
    return;
  }

  actions.saveSession({ sessionData });
}

function onUpdateServerTimeOffset(update: ApiUpdateServerTimeOffset) {
  setServerTimeOffset(update.serverTimeOffset);
}

function onUpdateCurrentUser<T extends GlobalState>(global: T, update: ApiUpdateCurrentUser) {
  const { currentUser, currentUserFullInfo } = update;

  global = {
    ...updateUser(global, currentUser.id, currentUser),
    currentUserId: currentUser.id,
  };
  global = updateUserFullInfo(global, currentUser.id, currentUserFullInfo);
  setGlobal(global);

  updateSessionUserId(currentUser.id);
  setClientDiagnosticsUserId(currentUser.id);
  callEventLoginNative({ channelCode: getChannelCode(), uid: currentUser.id });
  reportAttrLogin(currentUser.id);
  reportPushApp(currentUser.id);
}
